import {
  DescriptionOutlined,
  PeopleOutline,
  AttachMoneyOutlined,
  TrendingUpOutlined,
  CheckCircleOutlined,
  CalendarTodayOutlined,
} from '@mui/icons-material';
import { Tabs, MetricCard } from 'finara-components';
import type { TabItem } from 'finara-components';
import { useState } from 'react';

import { Navbar } from '@/components/admin/general/Navbar';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { reportMetrics } from '@/data/admin/reports/reportsData';
import { useAuth } from '@/hooks/useAuth';
import { formatNumber } from '@/utils/formatters';

import { GeneralReportsTab } from './components/GeneralReportsTab';
import { IndividualReportsTab } from './components/IndividualReportsTab';

export const ReportsPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('reportes-generales');

  const tabs: TabItem[] = [
    {
      id: 'reportes-generales',
      title: 'Reportes Generales',
      onSelect: () => setActiveTab('reportes-generales'),
    },
    {
      id: 'reportes-individuales',
      title: 'Reportes Individuales',
      onSelect: () => setActiveTab('reportes-individuales'),
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'reportes-generales':
        return <GeneralReportsTab />;
      case 'reportes-individuales':
        return <IndividualReportsTab />;
      default:
        return <GeneralReportsTab />;
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar
        navItems={adminNavItems}
        userName={user?.name || 'Usuario'}
        pageTitle="Reportes y Análisis"
        section="Reportes"
      />

      <div className="px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="lg:!flex items-center justify-between">
            <div className="mb-4 lg:!mb-0">
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <DescriptionOutlined />
                Reportes Empresariales
              </h1>
              <p className="text-gray-600 mt-1">
                Centro de reportes empresariales para análisis de empleados, operaciones y
                cumplimiento interno.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                <CalendarTodayOutlined fontSize="small" />
                Enero 2024
              </button>
            </div>
          </div>
        </div>

        {/* Metrics Row */}
        <div className="grid grid-cols-1 md:!grid-cols-4 gap-6 mb-6">
          <MetricCard
            title="Empleados Activos"
            value={reportMetrics.activeEmployees.toString()}
            icon={<PeopleOutline className="text-blue-600" />}
            variant="white"
          />
          <MetricCard
            title="Volumen Mensual"
            value={`$${formatNumber(reportMetrics.monthlyVolume)}`}
            icon={<AttachMoneyOutlined className="text-green-600" />}
            variant="white"
          />
          <MetricCard
            title="Adelantos Activos"
            value={reportMetrics.activeAdvances.toString()}
            icon={<TrendingUpOutlined className="text-purple-600" />}
            variant="white"
          />
          <MetricCard
            title="Tasa de Éxito"
            value={`${reportMetrics.successRate}%`}
            icon={<CheckCircleOutlined className="text-green-600" />}
            variant="white"
          />
        </div>

        {/* Tabs */}
        <div className="mb-6 bg-white rounded-lg">
          <Tabs tabs={tabs} activeTabId={activeTab} />
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};
